/**
 * Conflict types
 * Pattern: Same structure as types/division.ts
 */

export type ConflictStatus = 'open' | 'acknowledged' | 'resolved';
export type ConflictSeverity = 'info' | 'warning' | 'error' | 'blocking';
export type DetectionType = 'system' | 'user' | 'import';

export interface ConflictType {
  id: number;
  code: string;
  name: string;
  description: string;
  entity_type: string;
  severity: ConflictSeverity;
  blocks_workflow: boolean;
  auto_detect: boolean;
  requires_manual_resolution: boolean;
  resolver_roles: string[];
  notification_template: string | null;
  is_active: boolean;
}

export interface Conflict {
  id: number;
  conflict_type_id: number;
  conflict_type_code: string;
  conflict_type_name: string;
  conflict_type_description?: string;
  conflict_type_severity: ConflictSeverity;
  blocks_workflow: boolean;
  resolver_roles: string[];
  notification_template?: string;
  entity_type: string;
  entity_id: number;
  status: ConflictStatus;
  severity: ConflictSeverity;
  context: Record<string, unknown>;
  detected_at: string;
  detected_by_type: DetectionType;
  detected_by_user_id: number | null;
  resolved_at: string | null;
  resolved_by: number | null;
  resolution_type: string | null;
  resolution_data: Record<string, unknown> | null;
  resolution_notes: string | null;
  email_sent: boolean;
  email_sent_at: string | null;
  email_recipients: string[] | null;
  entity_info?: EntityInfo;
  audit_log?: AuditLogEntry[];
}

export interface ConflictContractOwner {
  user_id: number;
  user_name: string;
  division_id: number | null;
  division_name: string | null;
}

// A contract inside a meeting-level conflict's entity_info
export interface ConflictMeetingContract {
  id: number;
  contract_number: string;
  name: string;
  supplier_id: number | null;
  supplier_name: string | null;
  owners: ConflictContractOwner[];
}

export interface EntityInfo {
  id: number;
  contract_number?: string;
  contract_name?: string;
  // Contract-level conflicts only; null when the contract has no supplier
  supplier_id?: number | null;
  supplier_name?: string;
  division_id?: number;
  division_name?: string;
  // Live owners (contract-level conflicts); conflict.context only holds the detection-time snapshot
  contract_owners?: ConflictContractOwner[];
  // Live contracts of the meeting (meeting-level conflicts)
  contracts?: ConflictMeetingContract[];
  scheduled_datetime?: string;
  assessor_name?: string;
  full_name?: string;
  email?: string;
}

export interface AuditLogEntry {
  id: number;
  conflict_id: number;
  action: string;
  actor_id: number | null;
  actor_type: 'system' | 'user';
  actor_name?: string;
  previous_status: ConflictStatus | null;
  new_status: ConflictStatus | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

export interface ConflictFilters {
  status?: ConflictStatus;
  entityType?: string;
  conflictTypeCode?: string;
  limit?: number;
  offset?: number;
  search?: string;
  // JSON from useColumnFilters().getApiParam()
  columnFilters?: string;
  sortBy?: string;
  order?: 'asc' | 'desc';
  // IANA zone the Detected date filter counts days in
  timezone?: string;
}

export type ConflictExportFilters = Omit<ConflictFilters, 'limit' | 'offset'>;

export interface ConflictExportResponse {
  total: number;
  data: Conflict[];
}

export interface WorklistResponse {
  conflicts: Conflict[];
  total: number;
}

export interface ConflictStats {
  open: number;
  acknowledged: number;
  resolvedToday: number;
  totalResolved: number;
}

export interface ResolveConflictData {
  resolutionType: string;
  resolutionData?: Record<string, unknown>;
  notes?: string;
}

// MISSING_CONTRACT_ROLE: one contact assigned as CO or CM without the Contract Role
export type MissingRoleField = 'CO' | 'CM';

export interface MissingRoleAssignment {
  contact_id: number;
  contact_name: string;
  field: MissingRoleField;
}

// Division conflict specific context
export interface DivisionConflictContext {
  contract_number: string;
  division_ids: number[];
  division_names: string[];
  contract_owners: {
    user_id: number;
    user_name: string;
    division_name: string;
  }[];
}

// Resolution types
export const RESOLUTION_TYPE = {
  MANUAL_OVERRIDE: 'manual_override',
  DATA_FIX: 'data_fix',
  CO_REMOVED: 'co_removed',
  CO_CHANGED: 'co_changed',
  DIVISION_ASSIGNED: 'division_assigned',
  DEFERRED: 'deferred',
  IGNORED: 'ignored'
} as const;

export const CONFLICT_TYPE_CODE = {
  DIVISION_CONFLICT: 'DIVISION_CONFLICT',
  TIER_MISMATCH: 'TIER_MISMATCH',
  ASSESSOR_MISMATCH: 'ASSESSOR_MISMATCH',
  CM_MISMATCH: 'CM_MISMATCH',
  USER_INACTIVE: 'USER_INACTIVE',
  ATTENDEE_DECLINED: 'ATTENDEE_DECLINED',
  NO_RESPONSE: 'NO_RESPONSE'
} as const;
